import { useState } from "react";
import { router } from "@inertiajs/react";
import { BackofficeLayout } from "@/components/layout/BackofficeLayout";
import { DetailGrid } from "@/components/backoffice/DetailGrid";
import { Button } from "@/components/ui/button";
import { StageBadge } from '@/components/shared/StageBadge';
import { PaymentSummary } from '@/components/shared/PaymentSummary';
import { CalendarEventsSection } from '@/components/shared/CalendarEventsSection';
import { useConfirm } from "@/components/ui/confirm-dialog";
import { DocumentUploader } from "@/components/shared/DocumentUploader";
import { TemplateList } from "@/components/shared/TemplateList";
import { AddProtectedPersonSheet } from "../../NomineeShareholders/sheets/AddProtectedPersonSheet";
import { EditProtectedPersonSheet } from "../../NomineeShareholders/sheets/EditProtectedPersonSheet";
import type {
  BackofficeNomineeShareholderDetailed,
  NomineeShareholderTypeSummary,
} from "@/types";
import { CheckCircle, XCircle, UserPlus, Trash2 } from "lucide-react";
import { formatDate } from '@/lib/utils';

type LocationItem = { id: string; code: string; name: string };
type StateItem = LocationItem & { country_id: string };
type LocalAreaItem = LocationItem & { state_id: string };

type CalendarEvent = {
  id: string;
  title: string;
  due_date: string;
  source: string;
  completed: boolean;
  complete_path?: string;
  delete_path?: string;
};

type Props = {
  nominee_shareholder: BackofficeNomineeShareholderDetailed;
  type: NomineeShareholderTypeSummary;
  account: { id: string; name: string };
  templates: { id: string; name: string; file_url: string | null }[];
  countries: LocationItem[];
  states: StateItem[];
  local_areas: LocalAreaItem[];
  calendar_events: CalendarEvent[];
};

export default function BackofficeNomineeShareholdersShow({
  nominee_shareholder,
  type,
  account,
  templates,
  countries,
  states,
  local_areas,
  calendar_events,
}: Props) {
  const confirm = useConfirm();
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [editPerson, setEditPerson] = useState<
    (typeof nominee_shareholder.protected_persons)[number] | null
  >(null);

  const handleActivate = async () => {
    if (
      !(await confirm({
        title: "Activate Service",
        description: "Activate this nominee shareholder service?",
        confirmLabel: "Activate",
      }))
    )
      return;
    router.post(
      `/backoffice/nominee_shareholders/${nominee_shareholder.id}/activation`,
    );
  };

  const handleTerminate = async () => {
    if (
      !(await confirm({
        title: "Terminate Service",
        description:
          "Terminate this nominee shareholder service? This cannot be undone.",
        confirmLabel: "Terminate",
        destructive: true,
      }))
    )
      return;
    router.post(
      `/backoffice/nominee_shareholders/${nominee_shareholder.id}/termination`,
    );
  };

  return (
    <BackofficeLayout
      title={nominee_shareholder.business_name}
      breadcrumbs={[
        { label: "Backoffice", href: "/backoffice/dashboard" },
        {
          label: "Nominee Shareholders",
          href: "/backoffice/nominee_shareholders",
        },
        { label: nominee_shareholder.business_name },
      ]}
    >
      <div className="space-y-6">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">
              {nominee_shareholder.business_name}
            </h1>
            <StageBadge stage={nominee_shareholder.stage} variant="service" />
          </div>
          <p className="text-sm text-muted-foreground">
            {type.name} · {account.name}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {nominee_shareholder.stage === "payment" &&
            nominee_shareholder.payment?.paid && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleActivate}
                className="h-8 text-[13px]"
              >
                <CheckCircle className="mr-1.5 h-3 w-3" />
                Activate Service
              </Button>
            )}
          {nominee_shareholder.stage === "active" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleTerminate}
              className="h-8 text-[13px] text-destructive hover:text-destructive"
            >
              <XCircle className="mr-1.5 h-3 w-3" />
              Terminate Service
            </Button>
          )}
        </div>

        <DetailGrid
          items={[
            { label: "Type", value: type.name },
            {
              label: "Jurisdiction",
              value: type.jurisdiction_label || type.jurisdiction_code || "—",
            },
            {
              label: "Duration",
              value: `${nominee_shareholder.duration_months} months`,
            },
            { label: "Account", value: account.name },
            {
              label: "Activated",
              value: nominee_shareholder.activated_at
                ? formatDate(nominee_shareholder.activated_at)
                : "—",
            },
            {
              label: "Ended",
              value: nominee_shareholder.ended_at
                ? formatDate(nominee_shareholder.ended_at)
                : "—",
            },
          ]}
        />

        {/* Payment */}
        <PaymentSummary
          payment={nominee_shareholder.payment}
          adminActions={nominee_shareholder.payment ? {
            invoicePath: `/backoffice/nominee_shareholders/${nominee_shareholder.id}/payments/${nominee_shareholder.payment.id}/invoice`,
            confirmPaymentPath: `/backoffice/nominee_shareholders/${nominee_shareholder.id}/payments/${nominee_shareholder.payment.id}/payment_confirmation`,
            refundPath: `/backoffice/nominee_shareholders/${nominee_shareholder.id}/payments/${nominee_shareholder.payment.id}/refund/new`,
          } : undefined}
        />

        {/* Protected Persons */}
        <section className="border rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-sm font-medium text-foreground">
              Protected Persons
            </h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setAddPersonOpen(true)}
            >
              <UserPlus className="w-3.5 h-3.5 mr-1" />
              Add person
            </Button>
          </div>
          <div className="px-4">
            {nominee_shareholder.protected_persons.length > 0 ? (
              <div className="divide-y">
                {nominee_shareholder.protected_persons.map((person) => (
                  <div
                    key={person.id}
                    className="group flex items-center justify-between py-3 cursor-pointer hover:bg-muted/50 -mx-4 px-4 transition-colors"
                    onClick={() => setEditPerson(person)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <span className="text-xs font-medium text-muted-foreground">
                          {person.full_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground truncate">
                          {person.full_name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {person.email && <span>{person.email} · </span>}
                          <span className="capitalize">
                            {person.person_kind}
                          </span>
                          {person.ownership_percentage !== null &&
                            ` · ${person.ownership_percentage}%`}
                          {person.shares_held !== null &&
                            ` · ${person.shares_held} shares`}
                          {person.share_class && ` · ${person.share_class}`}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive shrink-0"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (
                          await confirm({
                            description: "Remove this person?",
                            confirmLabel: "Remove",
                            destructive: true,
                          })
                        ) {
                          router.delete(
                            `/backoffice/nominee_shareholders/${nominee_shareholder.id}/protected_persons/${person.id}`,
                          );
                        }
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-6 text-center">
                <p className="text-sm text-muted-foreground mb-2">
                  No protected persons added yet
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddPersonOpen(true)}
                >
                  Add first person
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Document Templates */}
        <TemplateList title="Document Templates" templates={templates || []} />

        {/* Application Documents */}
        <DocumentUploader
          title="Application Documents"
          documents={nominee_shareholder.application_documents || []}
          uploadPath={`/backoffice/nominee_shareholders/${nominee_shareholder.id}/application_documents`}
          deletePath={(docId) =>
            `/backoffice/nominee_shareholders/${nominee_shareholder.id}/application_documents/${docId}`
          }
        />

        {/* Active Documents */}
        <DocumentUploader
          title="Active Documents"
          documents={nominee_shareholder.active_documents || []}
          uploadPath={`/backoffice/nominee_shareholders/${nominee_shareholder.id}/active_documents`}
          deletePath={(docId) =>
            `/backoffice/nominee_shareholders/${nominee_shareholder.id}/active_documents/${docId}`
          }
        />

        {/* Calendar Events */}
        <CalendarEventsSection events={calendar_events || []} />
      </div>

      {/* Sheets */}
      <AddProtectedPersonSheet
        shareholderId={nominee_shareholder.id}
        countries={countries || []}
        states={states || []}
        localAreas={local_areas || []}
        open={addPersonOpen}
        onOpenChange={setAddPersonOpen}
        basePath="/backoffice"
      />
      <EditProtectedPersonSheet
        shareholderId={nominee_shareholder.id}
        person={editPerson}
        countries={countries || []}
        states={states || []}
        localAreas={local_areas || []}
        open={!!editPerson}
        onOpenChange={(open) => {
          if (!open) setEditPerson(null);
        }}
        basePath="/backoffice"
      />
    </BackofficeLayout>
  );
}
